import { useThree } from '@react-three/fiber';
import { useCallback, useContext, useEffect } from 'react';
import { Mesh, Object3D } from 'three';

import * as THREE from 'three';
import { TSceneChild } from '../../@types/Components';
import { EActiveDrawer } from '../../@types/Enums';
import useUndoRedo from '../../hooks/useUndoRedo';
import PlanModel from '../../models/standard/PlanModel';
import { useAnnotations } from '../../provider/AnnotationsProvider';
import { useBuilding } from '../../provider/BuildingProvider/BuildingProvider';
import { useDrawing } from '../../provider/DrawingProvider';
import { usePlan } from '../../provider/PlanProvider';
import { SceneDataContext } from '../../provider/SceneDataProvider';
import { useSelection } from '../../provider/SelectionProvider';
import { useValidation } from '../../provider/ValidationProvider';
import { clearStorageKey, LOCAL_STORAGE_KEY } from '../../utils/localStorageUtils';

const ClearCanvas = (): null => {
  const { gl } = useThree();
  const { sceneRef, setSceneNeedsClearing } = useContext(SceneDataContext);
  const { setHasIntersectingWalls } = useValidation();
  const { resetBuildingToDefaults, setPanoMeasurements } = useBuilding();
  const { planModel, setPlanModel } = usePlan();
  const { setIsDrawing, setRoomCompleted } = useDrawing();
  const { setActiveDrawer } = useSelection();
  const { resetAnnotations } = useAnnotations();
  const { resetHistory } = useUndoRedo();

  // Function to dispose and remove children from the scene
  const disposeAndRemoveChildren = useCallback(
    (children: TSceneChild[]) => {
      for (const object of children) {
        if (
          // BUG: some of these filters do not work anymore
          object.type !== 'AxesHelper' && // Will not find the axes, since its type is "Group"; instead maybe find by name
          object.type !== 'GridHelper' && // Will not find the grid, since its type is "Group"; instead maybe find by name
          object.type !== 'PointLight' &&
          object.type !== 'AmbientLight' &&
          object.name !== 'pointerEventMesh' &&
          object instanceof THREE.GridHelper
        ) {
          if (object instanceof Mesh) {
            // If object has geometries, dispose of them individually, which frees the allocated memory.
            if (object.geometry) {
              object.geometry.dispose();
            }
            // If object has materials, dispose of them as well.
            if (object.material) {
              // If there are multiple we iterate over them.
              if (Array.isArray(object.material)) {
                for (const material of object.material) {
                  material.dispose();
                }
              } else {
                // Otherwise we can just delete it.
                object.material.dispose();
              }
            }
          }
          if (object instanceof Object3D) {
            // Recursively calling this method again if the object has children itself.
            if (object.children.length !== 0) {
              disposeAndRemoveChildren(object.children);
            }
          }
          // And finally remove the object from the scene completely.
          sceneRef.current!.remove(object as Object3D);
        }
      }
    },
    [sceneRef],
  ); // Add other dependencies if there are any

  useEffect(() => {
    if (sceneRef.current !== null) {
      // Because scene.traverse() causes issues when altering the scene graph from within the callback, we recursively call the function below.
      disposeAndRemoveChildren(sceneRef.current!.children);
    }
    const regionToKeep = planModel?.getRegion() || 'DE';
    // Resetting the building & annotation to its default values.
    resetBuildingToDefaults();
    setPanoMeasurements([]);
    resetAnnotations();
    resetHistory();
    // Resetting other states.
    setIsDrawing(false);
    setRoomCompleted(false);
    setHasIntersectingWalls(false);
    setActiveDrawer(EActiveDrawer.NONE);
    clearStorageKey(LOCAL_STORAGE_KEY);
    const freshPlan = new PlanModel('');
    freshPlan.setRegion(regionToKeep);
    setPlanModel(freshPlan);

    // Manually clearing the renderer buffers for color, depth and stencil.
    gl.clear();
    // And to prevent additional execution of this Component, we reset the flag.
    setSceneNeedsClearing(false);
  }, [
    disposeAndRemoveChildren,
    gl,
    sceneRef,
    resetBuildingToDefaults,
    setActiveDrawer,
    setIsDrawing,
    setRoomCompleted,
    setSceneNeedsClearing,
    setHasIntersectingWalls,
    resetAnnotations,
    resetHistory,
    planModel?.getRegion(),
  ]);

  return null;
};

export default ClearCanvas;
